import * as React from 'react';
import { Currency } from 'core/utils';
import { Environment } from 'core/utils';
import { EnterButton, FunctionButton, NumericButton, ShowKeypadButton } from './Keypad.Button';
import { Box, Grid } from '@chakra-ui/react';
import * as Images from 'pos/Images';
import { ModalUtils } from 'core/ui-next';

type KeypadProps = {
	onKeyPressed?: (button: string) => void;
	alwaysVisible?: boolean;
	hideFunctionButtons?: boolean;
	isFullScreen: boolean;
	isLargeDevice: boolean;
};


// En PosScreen existe un método, useKeypad, que indica si se debe mostrar el teclado
// o no al hacer foco en los inputs. En las clases hijas se puede redifinir este método
// si no se quiere usar nunca el Keypad porque se va a usar el teclado del sistema.
//
// Cuando en una pantalla queremos usar el Keypad sólo para algunos controles, por ejemplo
// para introducir una cantidad pero no para introducir unas anotaciones, se pueden marcar
// los controles que no usan el keypad con la clase css disable-keypad.
export const Keypad: React.FC<KeypadProps> = ({ onKeyPressed, alwaysVisible, hideFunctionButtons, isFullScreen, isLargeDevice }) => {

	const [isVisible, setIsVisible] = React.useState(false);

	const ref = React.useRef<HTMLDivElement>(null);
	const lastFocusedInput = React.useRef<HTMLInputElement | null>(null);
	const firstInputEvent = React.useRef(true);

	const stopTrackingInput = React.useCallback(() => {
		lastFocusedInput.current?.classList?.remove?.('selected');
		lastFocusedInput.current = null;
		setIsVisible(false);

	}, [setIsVisible]);

	const startTrackingInput = React.useCallback((input: HTMLInputElement) => {
		if (input === lastFocusedInput.current) {
			input.focus();
			return;
		}

		// TODO: Esto habrá que quitarlo cuando no tengamos pantallas antiguas
		lastFocusedInput.current?.classList.remove('selected');
		input.classList.add('selected');

		lastFocusedInput.current = input;
		firstInputEvent.current = true;

		setIsVisible(true);
		input.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, [setIsVisible]);

	const isKeypadEvent = (ev: Event) => ref.current?.contains(ev.target as Node);

	const handleButtonClicked = React.useCallback((button: string) => {
		onKeyPressed?.(button);

		if (button === '{arrowdown}') {
			stopTrackingInput();
			return;
		}

		if (lastFocusedInput.current) {
			triggerKeyboardEvent(lastFocusedInput.current, button, firstInputEvent.current);
			firstInputEvent.current = false;
		}
	}, [onKeyPressed, stopTrackingInput]);

	React.useEffect(() => {
		const screenNode = ModalUtils.getParent(ref.current!);
		const observer = new MutationObserver(mutations => {
			if (mutations.flatMap(m => Array.from(m.addedNodes)).length) {
				setupInputs(screenNode);
			}
		});

		observer.observe(screenNode, { childList: true, subtree: true });
		setupInputs(screenNode);

		const handleScreenInteraction = (ev: Event) => {
			if (isKeypadEvent(ev)) return;

			if (shouldHandleKeysFor(ev.target as HTMLElement)) {
				startTrackingInput(ev.target as HTMLInputElement);
			} else if (ev.type === 'click') {
				// Solo llamamos a stopTrackingInput en eventos de click
				stopTrackingInput();
			}
		};

		// No me sirve basarme en focusin/focusout porque en safari cuando
		// pulsas fuera de un input no pierde el foco, y no se podría ocultar
		// el teclado. Me baso en click y si se toca fuera de un input oculto el teclado.
		screenNode.addEventListener('click', handleScreenInteraction);

		// Cubro también el caso en que llega el foco a un input sin hacer click,
		// por ejemplo al crear una vista, al pulsar enter en otro control, o de
		// cualquier forma "programática".
		screenNode.addEventListener('focusin', handleScreenInteraction);

		if (shouldHandleKeysFor(document.activeElement as HTMLElement)) {
			startTrackingInput(document.activeElement as HTMLInputElement);
		}

		return () => {
			observer.disconnect();
			screenNode.removeEventListener('click', handleScreenInteraction);
			screenNode.removeEventListener('focusin', handleScreenInteraction);
		};
	}, [startTrackingInput, stopTrackingInput]);

	const isSmallOrMediumScreenOnLargeDevice = !isFullScreen && isLargeDevice;
	const shouldAlwaysBeVisible = alwaysVisible || isSmallOrMediumScreenOnLargeDevice;
	const shouldShow = shouldAlwaysBeVisible || isVisible;

	let keypadLayout = shouldAlwaysBeVisible
		? <Grid templateColumns='1fr 1fr 1fr 4.5rem' templateRows='repeat(4, 3rem)' gap={2} w='full'>
			<Box gridRow={1} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='7' /></Box>
			<Box gridRow={1} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='8' /></Box>
			<Box gridRow={1} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='9' /></Box>
			<Box gridRow={2} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='4' /></Box>
			<Box gridRow={2} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='5' /></Box>
			<Box gridRow={2} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='6' /></Box>
			<Box gridRow={3} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='1' /></Box>
			<Box gridRow={3} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='2' /></Box>
			<Box gridRow={3} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='3' /></Box>
			<Box gridRow={4} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='0' /></Box>
			<Box gridRow={4} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='{numpaddecimal}' /></Box>
			<Box gridRow={4} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='{numpadsubtract}' /> </Box>
			<Box gridRow={1} gridColumn={4}><FunctionButton handleButton={handleButtonClicked} button='{clr}' src={Images.ClearCalculator} /></Box>
			<Box gridRow={'2 / span 3'} gridColumn={4} ><EnterButton handleButton={handleButtonClicked} /></Box>
		</Grid>
		: <Grid templateColumns='1fr 1fr 1fr 4.5rem' templateRows='repeat(4, 3rem)' gap={2} w='full'>
			<Box gridRow={1} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='7' /></Box>
			<Box gridRow={1} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='8' /></Box>
			<Box gridRow={1} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='9' /></Box>
			<Box gridRow={2} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='4' /></Box>
			<Box gridRow={2} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='5' /></Box>
			<Box gridRow={2} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='6' /></Box>
			<Box gridRow={3} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='1' /></Box>
			<Box gridRow={3} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='2' /></Box>
			<Box gridRow={3} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='3' /></Box>
			<Box gridRow={4} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='0' /></Box>
			<Box gridRow={4} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='{numpaddecimal}' /></Box>
			<Box gridRow={4} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='{numpadsubtract}' /></Box>
			<Box gridRow={1} gridColumn={4}><ShowKeypadButton handleButton={handleButtonClicked} button='{arrowdown}' src={Images.Keypad} isShowKeypad={shouldShow} /></Box>
			<Box gridRow={2} gridColumn={4}><FunctionButton handleButton={handleButtonClicked} button='{clr}' src={Images.ClearCalculator} /> </Box>
			<Box gridRow={'3 / span 2'} gridColumn={4} ><EnterButton handleButton={handleButtonClicked} /></Box>
		</Grid>;

	if (hideFunctionButtons)
		keypadLayout = <Grid templateColumns='1fr 1fr 1fr 1fr' templateRows='repeat(4, 3rem)' gap={2} w='full'>
			<Box gridRow={1} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='7' /></Box>
			<Box gridRow={1} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='8' /></Box>
			<Box gridRow={1} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='9' /></Box>
			<Box gridRow={2} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='4' /></Box>
			<Box gridRow={2} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='5' /></Box>
			<Box gridRow={2} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='6' /></Box>
			<Box gridRow={3} gridColumn={1}><NumericButton handleButton={handleButtonClicked} button='1' /></Box>
			<Box gridRow={3} gridColumn={2}><NumericButton handleButton={handleButtonClicked} button='2' /></Box>
			<Box gridRow={3} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='3' /></Box>
			<Box gridRow={4} gridColumn={'1 / span 2'}><NumericButton handleButton={handleButtonClicked} button='0' /></Box>
			<Box gridRow={4} gridColumn={3}><NumericButton handleButton={handleButtonClicked} button='{numpaddecimal}' /></Box>
		</Grid>;

	return <Box ref={ref} display={shouldShow ? 'block' : 'none'} w='full' bg={{ base: '{colors.neutral.100}', _dark: '{colors.neutral.900}' }} padding={'2'}>
		{keypadLayout}
	</Box>;
};

function getUpdatedContent(oldContent: string, button: string, append = true): string {
	if (button === '{arrowdown}')
		return oldContent;

	if (button === '{clr}')
		return '';

	if (button === '{numpaddecimal}')
		return append ? oldContent.toString() + Currency.DECIMAL_SEPARATOR : Currency.DECIMAL_SEPARATOR;

	if (button === '{numpadsubtract}') {
		if (append)
			return oldContent.startsWith('-') ? oldContent.slice(1) : '-' + oldContent.toString();
		return '-';
	}

	return append ? oldContent.toString() + button.toString() : button.toString();
}

function triggerKeyboardEvent(input: HTMLInputElement, button: string, firstInputEvent: boolean) {

	if (button === '{enter}') {
		input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: false }));
		input.dispatchEvent(new KeyboardEvent('keypress', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: false }));
		input.dispatchEvent(new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: false }));
		return;
	}

	const updatedContent = getUpdatedContent(input.value, button, !firstInputEvent);

	// se supone que esto ya lo mira react el solito para lanzar o no su evento de mentira
	if (updatedContent !== input.value) {

		// Hago primero el setValue en el input destino para evitarme problemas con inputs
		// sincronizados que se "quedan atrás"
		const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value')!.set;
		nativeSetter!.call(input, updatedContent);

		input.dispatchEvent(new KeyboardEvent('keydown', { key: button, code: button, bubbles: true, cancelable: false }));
		input.dispatchEvent(new KeyboardEvent('keypress', { key: button, code: button, bubbles: true, cancelable: false }));
		input.dispatchEvent(new KeyboardEvent('keyup', { key: button, code: button, bubbles: true, cancelable: false }));
		input.dispatchEvent(new Event('input', { bubbles: true }));
	}
}

function setupInputs(screenNode: HTMLElement) {
	// Coge todos los inouts de la pantalla y si debe manejar los Keys los pone en readOnly para deshabilitar el 
	// teclado nativo. Actualmente maneja los inputs de tipo number, text y password
	Array.from(screenNode.querySelectorAll('input'))
		.filter(shouldHandleKeysFor)
		// Ponemos los inputs a readOnly para que no salga el teclado nativo y se pegue con nuestro teclado
		.forEach(input => input.readOnly = Environment.isMobile());
}

function isInput(target: EventTarget): target is HTMLInputElement {
	return (target as HTMLInputElement).nodeName === 'INPUT';
}

function shouldHandleKeysFor(target: Nullable<HTMLElement>): boolean {

	if (target === null || !isInput(target))
		return false;

	if (target.disabled)
		return false;

	const input = (target as HTMLInputElement);

	const classes = input.classList;
	if (classes.contains('disable-keypad'))
		return false;

	const canHandleInputType = ['text', 'number', 'password'].includes(input.type);
	return canHandleInputType;
}